import {makeRequest} from "../baseApi";
import {getBookIdWithPrefix, getBookIdNumber} from "../../utils/content/content";
import {
  parseResponseBibleContents,
  parseResponseCheckContentVersion,
  parseResponseContentPreview
} from "./utils";
import {getBookIdByCode, getOrderNumberOfMonth, parseResponseDefault} from "../not-sorted.utils";
import {LoadDirection} from "./constants";
import {AdvSearchNavigationRequest} from "../search/advanced/requests";
import {getSearchSuggestionsShortcutRequest} from "../search/suggestions/requests";
import {BOOK_PERMISSION} from "../../utils/content/content.constants";
import {dictionaryDefaultFilter} from "../../utils/search/search.constants";
import { makeParamsString } from "../utils";
import { getBiblePubnr } from "@/shared/utils/search/advanced/advanced";

export const getContentPreviewBible = (bibleId, targetBibles, targetParagraphs) => {
  let url = `content/preview/bible/${getBookIdNumber(bibleId)}?`;

  const pubnrs = targetBibles.map((item) => "pubnr=" + getBookIdNumber(item));
  if (pubnrs.length) {
    url += pubnrs.join("&");
  }

  const paras = targetParagraphs.map((item) => {
    const para = item.indexOf(".") !== -1 ? item.split(".")[1] : item;
    return "para=" + para;
  });
  if (paras.length) {
    url += "&" + paras.join("&");
  }

  return makeRequest({
    url,
    memoryCache: true,
    parseResponse: parseResponseBibleContents,
  });
};

export const getContentPreviewRequest = (paraId, langs) => {
  let url = `content/preview/${paraId}`;

  if (langs) {
    const langParams = langs.map((item) => "lang=" + item);
    url += "?" + langParams.join("&");
  }

  return makeRequest({
    url,
    memoryCache: true,
    parseResponse: parseResponseContentPreview,
  });
};

export const getParaByPeriodRefCode = async (refs, periodicals, book) => {
  const [pubnr, year, month, day, paragraph] = refs;

  const defaultExtras = {type: "periodicals", daterange: "all"};
  const extras = {...defaultExtras};
  const bookId = getBookIdByCode(periodicals, pubnr, book.book_id);

  if (!year) {
    if (!bookId) {
      return {status: "error", message: "codeNotFound"};
    }

    extras.pubnr = bookId;

    return await AdvSearchNavigationRequest(extras);
  }

  extras.date = `${year}-${getOrderNumberOfMonth(month)}-${day || "1"}`;
  if (bookId) {
    extras.pubnr = bookId;
  }
  extras.paragraph = paragraph || "1";

  const data = await AdvSearchNavigationRequest(extras);

  if (!data) {
    return {status: "error", message: "dateNotFound"};
  }

  return data;
};

export const resolveParaByRefCode = async (refs, periodicals, book, langs, baseBible) => {
  const isBible = book.realType === "bible";
  const isPeriodical = book.realType === "periodicals";

  if (isPeriodical) {
    return getParaByPeriodRefCode(refs, periodicals, book);
  }

  const [ref1, ref2, ref3] = refs;
  const params = {query: "", langs, bibleId: isBible ? getBiblePubnr(baseBible) : book.book_id};
  const delimiter = isBible ? ":" : ".";
  const postfix = _createPostfix(ref2, ref3, delimiter);
  params.query = postfix ? `${ref1} ${postfix}` : ref1;

  // if chapter is empty - change chapter and paragraph to "1"
  if (!ref2) {
    refs[1] = "1";
    refs[2] = "1";
  }

  return getParaByRefCodeRequest(refs, params, book.book_id, book.lang, isBible);
};

const _createPostfix = (ref2, ref3, delimiter) => {
  return [ref2 || 1, ref3 || 1].join(delimiter);
};

export const getParaByRefCodeRequest = async (refs, params, bookId, bookLang, isBible = false) => {
  const [ref1, ref2, ref3] = refs;

  let res = await getSearchSuggestionsShortcutRequest(
    params.query,
    params.bibleId,
    params.langs,
  );
  if (res?.para_id) {
    return res;
  }

  res = await getParaByRefCode({ref1, ref2, ref3, bookId, isBible, lang: bookLang});
  if (res?.para_id) {
    return res;
  }

  res = await getParaByRefCode({ref1, ref3: "1", bookId, isBible, lang: bookLang});
  if (res?.para_id) {
    return res;
  }

  res = await getParaByRefCode({ref1, ref3: "1", bookId, isBible: false});
  return res;
};

export const getParaByRefCode = ({ref1, ref2, ref3, bookId, isBible, lang, code}) => {
  const params = [];
  const type = isBible ? "bible" : "book";
  params.push(`type=${type}`);
  if (ref1) {
    params.push(isBible ? `bible_code=${ref1}` : `code=${ref1}`);
  }
  if (ref2) {
    params.push(isBible ? `chapter=${ref2}` : `page=${ref2}`);
  }
  if (ref3) {
    params.push(isBible ? `verse=${ref3}` : `paragraph=${ref3}`);
  }
  if (lang) {
    params.push(`lang=${lang}`);
  }
  if (code && isBible) {
    params.push(`code=${code}`);
  }
  if (bookId && isBible) {
    params.push(`book_id=${bookId}`);
  }

  return makeRequest({
    url: `content/utilities/paraid?${params.join("&")}`,
    memoryCache: true,
    parseResponse: parseResponseDefault,
  });
};

export const fetchDictionariesRequest = () => {
  const request = {
    // There was no dictionaries for non-english languages.
    url: "content/books?type=dictionary&lang=en",
    type: "get",
    parseResponse: (response) => {
      const options = response.data.results.map((item) => ({
        key: item.book_id,
        label: `${item.title} (${item.code})`,
        isForSale: item.action_required === BOOK_PERMISSION.purchased,
        id: getBookIdWithPrefix(item.para_id),
        className: "book",
      }));

      return [dictionaryDefaultFilter, ...options];
    },
  };
  return makeRequest(request);
};

const bibleExpireTime = 60 * 60 * 1000; // 1 hour

export const getBibleLanguagesRequest = () => {
  const url = "content/books/biblelang";
  return makeRequest({
    url,
    storageCacheName: url,
    storageCacheTime: bibleExpireTime,
    parseResponse: parseResponseDefault,
  });
};

export const getBibliographyRequest = (lang, page, order, extras) => {
  //const [bookId, chapterId] = paraId.split(".");
  if (extras["type"]) {
    delete extras["type"];
  }
  const paramsString = makeParamsString(extras, true);
  const searchExtras = paramsString ? `?${paramsString}&` : "?";
  let args =
    Object.entries(order).length !== 0
      ? "sort=" + order.sort + "&order=" + order.order + "&page=" + page
      : "sort=default&order=asc&page=" + page;

  const request = {
    url: `content/languages/${lang}/bibliography/${searchExtras}${args}`,
    type: "get",
    parseResponse: parseResponseDefault,
  };
  return makeRequest(request);
};

export const getParagraphsRequest = (
  paraId,
  limit,
  loadDirection = LoadDirection.both,
  highlight,
) => {
  const [bookId, chapterId] = paraId.split(".");
  let queries = ["trans=all"];

  if (limit) {
    queries.push("limit=" + limit);
  }
  if (loadDirection) {
    queries.push("direction=" + loadDirection);
  }
  if (highlight) {
    // Single quote replacing is a fix of:
    // `Darryl says that if we search phrase and in has a quote only
    // in the beginning or only in the end, then results are not highlighted in reader`
    const highlightFinal = (highlight.match(/"/g)?.length === 1)
      ? highlight.replace("\"", "") : highlight;
    queries.push("highlight=" + highlightFinal);
  }
  return makeRequest({
    url: `content/books/${bookId}/content/${chapterId}?` + queries.join("&"),
    memoryCache: true,
    parseResponse: parseResponseDefault,
  });
};

export const checkContentVersionRequest = () => {
  return makeRequest({
    url: "/content/version",
    parseResponse: parseResponseCheckContentVersion,
  });
};
